import React, {useContext} from 'react'
import './quote2.css'
import {carrierProductToFaceAmountOptionsBigdec} from 'ladder/carrier_product/face_amounts2'
import {Modal} from 'ladder/ux/modal2'
import {AddContactModal2} from 'ladder/agents/dashboard/contacts2'
import {
  CenteredCheckbox,
  DobSelect,
  HeightField,
  QuoterSelect,
  SingleSelect,
  StateSelect,
  WeightField,
} from 'ladder/agents/dashboard/inputs2'
import {Text} from 'ladder/ux/typography2'
import {fmtMoneyNoRound, fmtMoneyNoRoundNoWord} from 'ladder/common2'
import {FitCheck2} from 'ladder/agents/dashboard/PreScreener2'
import {NewLink} from 'ladder/ux/link2'
import {Primary} from 'ladder/ux/button2'
import {DashboardQueryContext} from "ladder/agents/dashboard/DashboardQueryContext";

function ClientSelfApplyCheckbox({isClientSelfApplyChecked, updateClientSelfApply}) {
  return <div>
    <CenteredCheckbox name='client-self-apply'
                      removeBorder
                      checked={isClientSelfApplyChecked}
                      onChange={updateClientSelfApply}
                      text='My client wants to fill out their own application'/>
    <Text block size='body-12' color='#707070' className='explain-self-apply'>
      Checking this box means you can no longer start an application on your client’s behalf.
    </Text>
  </div>
}

function ShareQuoteModalMainCopy({monthlyPremiumPriceRange}) {
  {
    if (!!monthlyPremiumPriceRange) {
      return <Text block size='body-12'>
        {'We’ll send your client an email with the price estimate for the '}
        <Text size='body-12' weight='bold'>
          {classLabelToFullName(monthlyPremiumPriceRange[0][0])}
        </Text>
        {' rate class. Please be sure they understand'}
        {' the final price will be determined by their responses on the full application,'}
        {' plus any relevant third party data we acquire.'}</Text>
    } else {
      return <Text block size='body-20'>
        {'We’ll email this quote to your client and update their status to '}
        <Text weight='bold'>Quote sent</Text>
        {'.'}
      </Text>
    }
  }
}

function ShareModal(props) {
  // the '--' placeholder at the top of the list doesn't count as a contact
  const noRealContactSelected = !props.selectedContactEmail || props.selectedContactEmail === '--'
  return props.isShareModalOpen &&
      <Modal show
             backgroundColor='off-white'
             horizontallyCentered
             roundedCorners
             width='l'
             height='l'
             title='Email this quote'
             onDismiss={props.onDismissShareModal}>
        <div className='share-modal'>
          <ShareQuoteModalMainCopy monthlyPremiumPriceRange={props.monthlyPremiumPriceRange}/>
          <div>
            <QuoterSelect name='Contact'
                          svgPath='agents/profile/first-name-icon'
                          floatingPlaceholder
                          placeholderText={props.agentContacts?.length ? 'My contacts' : 'No contacts yet'}
                          value={props.selectedContactEmail}
                          removeBorder
                          error={!!props.agentDashShareQuoteError}
                          values={[{
                            caption: '--', value: '--',
                          }, ...(props.agentContacts?.map(([name, email]) => ({caption: name, value: email})))]}
                          fullWidth
                          onChange={props.onSelectContact}/>
          </div>
          {!!props.agentDashShareQuoteError && <Text block size='body-12' color='red'>
            {props.agentDashShareQuoteError}
          </Text>}
          <NewLink className='add-new-contact-link'
                   underline={false}
                   textAlign='center'
                   size='body-15'
                   onClick={props.onClickAddNewContact}>
            Add a new contact instead
          </NewLink>
          <ClientSelfApplyCheckbox
              isClientSelfApplyChecked={props.isClientSelfApplyChecked}
              updateClientSelfApply={props.updateClientSelfApply}/>
          <div className='quoter-button'>
            <Primary name='send-quote'
                     disabled={noRealContactSelected}
                     fullWidth
                     onClick={props.onClickSendQuote}
                     textTransformDisabled
                     roundedCorners
                     loading={props.isQuoterLoading}>
              Send
            </Primary>
          </div>
        </div>
        <div style={{'marginTop': '2em'}}>
          {/* Missing field errors should go here, in red text*/}
        </div>
      </Modal>
}

const QuoterRules = ({agentTopLevelAgencyLddrSource}) => {
  const disallowedStates = (agentTopLevelAgencyLddrSource === 'prosperity-imo') ? 'Connecticut or New York' : 'New York'
  return (<div className='quoter-rules'>
    <Text color='#636363'
          fontSize='11px'
          weight='400'
          lineHeight='17px'
          style={{'fontStyle': 'italic'}}>
      You must hold a life insurance producer license for each jurisdiction where you solicit customers for Ladder
      products, including use of the estimate tool below. You may not solicit customers for Ladder products
      in {disallowedStates}, regardless of licensing status.
    </Text>
  </div>)
}

function PreScreener({answerQuestion, redirectStartQuote, preScreenerAnswers}) {
  return <div className='quoter-and-app-button'>
    <div className='section'>
      <Text tag='h5' size='header-15'>Quick fit check</Text>
      <Text block size='body-12' weight='light' className='quoter-copy'>
        Answer a few questions to find out if we’re a fit for your client.
        <span style={{fontWeight: 'bold'}}> All applicants must be U.S. citizens or permanent residents. </span>
        Ladder does not issue replacement policies.
      </Text>
      <FitCheck2
          answerQuestion={answerQuestion}
          redirectStartQuote={redirectStartQuote}
          preScreenerAnswers={preScreenerAnswers}/>
    </div>
  </div>
}

function GetAQuoteButton({onClick, loading, errors}) {
  return <div>
    <Primary name='get-quote'
             className='button-drop-shadow'
             fullWidth
             loading={loading}
             disabled={!!(errors?.length)}
             roundedCorners
             onClick={onClick}>
      Get a quote
    </Primary>
  </div>
}

function IneligibilityMessage({ineligibleReason}) {
  return <Text color='#CB3A3A'>
    {ineligibleReason === 'bmi' ? 'BMI too high/low' : 'User is ineligible'}
  </Text>
}

function classLabelToFullName(label) {
  switch (label) {
    case 'preferred-plus':
      return 'Preferred Plus'
    case 'preferred':
      return 'Preferred'
    case 'standard-plus':
      return 'Standard Plus'
    case 'standard':
      return 'Standard'
    case 'substandard-1':
      return 'Substandard (Table\u00a01)'
    case 'substandard-2':
      return 'Substandard (Table\u00a02)'
    case 'substandard-3':
      return 'Substandard (Table\u00a03)'
    case 'substandard-4':
      return 'Substandard (Table\u00a04)'
    case 'substandard-5':
      return 'Substandard (Table\u00a05)'
    case 'substandard-6':
      return 'Substandard (Table\u00a06)'
    case 'substandard-7':
      return 'Substandard (Table\u00a07)'
    case 'substandard-8':
      return 'Substandard (Table\u00a08)'
  }
}

function PriceRangeDetails({monthlyPremiumPriceRange}) {
  let [label] = monthlyPremiumPriceRange[0]
  let estimateName = classLabelToFullName(label)
  return <Text size='body-12'>
    Our best estimate for your client is{' '}
    <Text size='body-12' weight='bold'>{estimateName}</Text>
    . The range is based on the answers provided so far. The final price will be determined by the client’s full
    application and any relevant third party consumer report data we acquire.
  </Text>
}

function PriceCell({label, monthlyPremium}) {
  return <div className='cell'>
    <div className='label'>
      <Text size='header-10' weight='500'>
        {classLabelToFullName(label)}
      </Text>
      <Text desktopSize='header-18' mobileSize='header-15'>
        {fmtMoneyNoRound(monthlyPremium)}
      </Text>
    </div>
  </div>
}

function PriceRange({monthlyPremiumPriceRange}) {
  return <div className='price-range-container'>
    <Text desktopSize='body-15' mobileSize='body-12'>
      Price range (per month)
    </Text>
    <div className='prices-row'>
      {...(monthlyPremiumPriceRange.map(([label, monthlyPremium]) => (
          <PriceCell key={label} label={label} monthlyPremium={monthlyPremium}/>)))}
    </div>
    <PriceRangeDetails monthlyPremiumPriceRange={monthlyPremiumPriceRange}/>
  </div>
}

function MonthlyPremium({monthlyPremium}) {
  return <div className='monthly-premium'>
    <Text desktopSize='body-15' mobileSize='body-12'>Price</Text>
    <span>
      <Text desktopSize='header-32' mobileSize='header-20'>
        {fmtMoneyNoRound(monthlyPremium)}
      </Text>
      <Text desktopSize='body-20' mobileSize='body-15'>
        /mo.
      </Text>
        </span>
  </div>
}

function StartAppButton({onClickStartApplication, isQuoterLoading}) {
  return <Primary name='start-application'
                  className='shadow'
                  loading={isQuoterLoading}
                  disabled={isQuoterLoading}
                  fullWidth
                  roundedCorners
                  onClick={onClickStartApplication}>
    Start an application</Primary>
}

function QuoteResponseSection(props) {
  return <div className='quote-response-container'>
    {!!props.monthlyPremiumPriceRange && <PriceRange monthlyPremiumPriceRange={props.monthlyPremiumPriceRange}/> ||
        <MonthlyPremium monthlyPremium={props.monthlyPremium}/>}
    <div className='buttons'>
      <div className='start-app-button-container'>
        <StartAppButton
            onClickStartApplication={props.onClickStartApplication}
            isQuoterLoading={props.isQuoterLoading}
        />
      </div>
      <NewLink size='body-15' underline={false} onClick={props.onClickEmailQuote}>
        Email this quote</NewLink>
    </div>
  </div>
}

function Quoter(props) {
  const {quote, updateQuote, standaloneQuoterErrorMissingFields, errors, quoteResponse, onClickResetQuote} = props
  return <div className='share-quote-form-container'>
    <div className='columns'>
      <div className='column'>
        <SingleSelect
            options={[{
              'option-value': 'gender/male', 'option-label': 'Male', emoji: '👨',
            }, {'option-value': 'gender/female', 'option-label': 'Female', emoji: '👩'}]}
            value={quote.gender}
            onChange={updateQuote.gender}/>
        <DobSelect
            standaloneQuoterErrorMissingFields={standaloneQuoterErrorMissingFields}
            removeBorder='remove-border'
            height='m'
            onChange={updateQuote.dateOfBirth}
            quoteDateOfBirth={quote.dateOfBirth}/>
        <HeightField
            standaloneQuoterErrorMissingFields={standaloneQuoterErrorMissingFields}
            removeBorder
            height='l'
            onChange={updateQuote.height}
            quoteHeightInches={quote.height}/>
        <WeightField
            standaloneQuoterErrorMissingFields={standaloneQuoterErrorMissingFields}
            removeBorder='remove-border'
            height='s'
            onChange={updateQuote.weight}
            quoteWeightPounds={quote.weight}/>
      </div>
      <div className='column'>
        <StateSelect
            standaloneQuoterErrorMissingFields={standaloneQuoterErrorMissingFields}
            removeBorder
            height='l'
            onChange={updateQuote.state}
            quoteState={quote.state}
            required
            errors={errors}/>
        <CenteredCheckbox
            name='family-history'
            outerBorderColor='light-gray'
            removeBorder
            borderColor='light-gray'
            checked={!!quote.familyHistory}
            onChange={updateQuote.familyHistory}
            text='Biological parent or sibling diagnosed with cancer, diabetes, or heart disease before the age of 60'/>
        <CenteredCheckbox
            name='smoker'
            outerBorderColor='light-gray'
            removeBorder
            borderColor='light-gray'
            checked={!!quote.smoker}
            onChange={updateQuote.smoker}
            text='Used tobacco or nicotine in the last 3 years'/>
      </div>
    </div>

    {quoteResponse.haveValidQuoteResponse && <div className='columns'>
      <div className='column'>
        <QuoterSelect
            name='Face-amount'
            svgPath='agents/quoter/face-value-icon'
            floatingPlaceholder
            placeholderText='Face amount'
            value={quote.faceAmount}
            removeBorder
            values={[{
              caption: '--', value: null, disabled: true,
            }, ...carrierProductToFaceAmountOptionsBigdec(quoteResponse.carrierProduct)
                .map(val => ({caption: fmtMoneyNoRoundNoWord(val, 0), value: val}))]}
            onChange={updateQuote.faceAmount}
        />
      </div>
      <div className='column mobile-margin-top'>
        <QuoterSelect
            name='Term'
            svgPath='agents/quoter/calendar-icon'
            floatingPlaceholder
            placeholderText='Term'
            value={quote.term}
            removeBorder
            onChange={updateQuote.term}
            values={[{
              caption: '--', value: null, disabled: true,
            }, ...(quoteResponse.termsAvailable.map(val => ({caption: `${val} years`, value: val})))]}
        />
      </div>
    </div>}
    {!!quoteResponse.ineligibleReason && <IneligibilityMessage ineligibleReason={quoteResponse.ineligibleReason}/>}
    {quoteResponse.haveValidQuoteResponse && <QuoteResponseSection
        isQuoterLoading={props.isQuoterLoading}
        monthlyPremium={props.monthlyPremium}
        monthlyPremiumPriceRange={props.monthlyPremiumPriceRange}
        onClickStartApplication={props.onClickStartApplication}
        onClickEmailQuote={props.onClickEmailQuote}
    /> || <GetAQuoteButton
        onClick={props.onClickGetAQuote}
        loading={props.isQuoterLoading}
        errors={errors}
    />}
    <NewLink underline={false} textAlign='center' onClick={onClickResetQuote}>
      <Text mobileSize='body-15' desktopSize='body-15'>Reset</Text>
    </NewLink>
    <ShareModal {...props}/>
  </div>
}

function QuoterAndAppButton(props) {
  const {agentEnablePreScreener, onClickReturnToPreScreener} = props
  return <div className='quoter-and-app-button'>
    <div className='section'>
      <div className='title-row'>
        <Text tag='h5' size='header-15'>Get a quote</Text>
        {agentEnablePreScreener && (<NewLink
            size='body-15'
            onClick={onClickReturnToPreScreener}>
          Back to quick fit check
        </NewLink>)}
      </div>
      <Text block size='body-12' className='quoter-copy'>
        Answer a few questions to generate your client’s quote.
      </Text>
      <Quoter {...props}/>
    </div>
    <AddContactModal2
        isAddContactModalOpen={props.isAddContactModalOpen}
        isCreateContactLoading={props.isCreateContactLoading}
        addContactModalContact={props.addContactModalContact}
        addContactModalErrors={props.addContactModalErrors}
        addContactModalUpdateContact={props.addContactModalUpdateContact}
        onClickCreateContact={props.onClickCreateContact}
        onDismissAddContactModal={props.onDismissAddContactModal}/>
  </div>
}

export function QuotePageImpl(props) {
  const q = useContext(DashboardQueryContext)
  const {agentEnablePreScreener, agentDashPastPreScreener} = props
  let thingToDisplay
  if (agentEnablePreScreener && !agentDashPastPreScreener) {
    thingToDisplay = <PreScreener
        preScreenerAnswers={props.preScreenerAnswers}
        answerQuestion={props.answerQuestion}
        redirectStartQuote={props.redirectStartQuote}/>
  } else {
    thingToDisplay = <QuoterAndAppButton {...props}/>
  }
  return <div className='quote-page'>
    <Text tag='h3' weight='600' size='header-32' mobileSize='header-18'>Welcome to
      Ladder, {q['agent/first-name']}</Text>
    <div className='quoter-rules-container'>
      <QuoterRules agentTopLevelAgencyLddrSource={props.agentTopLevelAgencyLddrSource}/>
    </div>
    <div id='ladder-agents-quote-container'>{thingToDisplay}</div>
  </div>
}
